<template>
  <div class="map-layer-control">
    <div class="header" @click="toggleExpanded">
      <span>
        <translate>Layers</translate>
        <general-map-preferences
          v-if="$parent && $parent.generalMapPreferences"
          :preferences="$parent.generalMapPreferences"
          :available-layers="$parent.generalMapPreferencesOptions"
        />
      </span>
      <i :class="['fa', expanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
    </div>

    <div v-show="expanded" class="layer-list">
      <div
        v-for="(layer, key) in layers"
        :key="key"
        v-if="isLayerEnabled(key)"
        class="layer-item"
      >
        <label>
          <input
            type="checkbox"
            v-model="layers[key].visible"
            @change="toggleLayer(key)"
          >
          <span class="layer-label">{{ layer.label }}</span>

          <div class="layer-indicators">
            <span class="item-count" v-if="layer.count !== undefined">{{ layer.count }}</span>

            <div v-if="hasFilters(key)" class="filter-actions">
              <button
                type="button"
                :class="['action-btn filter-trigger', { 'has-active-filters': getActiveFiltersCount(key) > 0 }]"
                @click.stop.prevent="openFilters(key)"
                v-b-tooltip.hover.bottom="getActiveFiltersCount(key) > 0 ? $gettext('Edit active filters') : $gettext('Add filters')"
              >
                <font-awesome-icon icon="filter" />
                <span v-if="getActiveFiltersCount(key) > 0" class="filter-count-badge">
                  {{ getActiveFiltersCount(key) }}
                </span>
              </button>

              <button
                v-if="getActiveFiltersCount(key) > 0"
                type="button"
                class="action-btn reset-trigger"
                @click.stop.prevent="resetLayerFilters(key)"
                v-b-tooltip.hover.bottom="$gettext('Clear filters')"
              >
                <font-awesome-icon icon="trash-alt" />
              </button>
            </div>
          </div>
        </label>
      </div>
    </div>

    <layer-filters-modal
      id="layerFiltersMapControlModal"
      ref="layerFiltersModal"
      :layer-key="activeFiltersLayer || ''"
      :layer-label="activeFiltersLayer ? (mapLayersConfig[activeFiltersLayer].label || activeFiltersLayer) : ''"
      :filters-config="activeFiltersLayer ? (mapLayersConfig[activeFiltersLayer].filters || {}) : {}"
      :current-filters="localActiveFilters ? (localActiveFilters[activeFiltersLayer] || {}) : {}"
      @apply="applyFilters"
    />
  </div>
</template>

<script>
import MapService from '@/services/MapService'
import MapLayersConfigMixin from '@/mixins/mapLayersConfig.mixin'
import LayerFiltersModal from '@/components/MapsV2/Modals/LayerFiltersModal'
import GeneralMapPreferences from '@/components/MapsV2/Controls/GeneralMapPreferences'
import { cloneDeep } from 'lodash'

export default {
  emits: ['apply-filters'],
  name: 'MapLayerControl',
  components: {
    LayerFiltersModal,
    GeneralMapPreferences
  },
  mixins: [MapLayersConfigMixin],
  props: {
    layersVisibility: {
      type: Object,
      default: () => ({})
    },
    onVisibilityChange: {
      type: Function,
      default: () => {}
    },
    activeFilters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      expanded: true,
      layers: {
        couriers_self: { label: this.$gettext('Couriers'), visible: true, count: 0 },
        items_self: { label: this.$parent.ecosystemModules?.items?.label || this.$gettext('Items'), visible: true, count: 0 },
        tours_self: { label: this.$gettext('Tours'), visible: true, count: 0 },
        shippings_self: { label: this.$gettext('Shippings'), visible: true, count: 0 },
        shippings_shared: { label: this.$gettext('Shared Shippings'), visible: true, count: 0 },
        geofences_self: { label: this.$gettext('Geofences'), visible: true, count: 0 }
      },
      activeFiltersLayer: null,
      localActiveFilters: cloneDeep(this.activeFilters)
    }
  },
  mounted() {
    // Sync with external state if provided
    if (this.layersVisibility) {
      Object.keys(this.layersVisibility).forEach(key => {
        if (this.layers[key]) {
          this.layers[key].visible = this.layersVisibility[key]
          // Sync MapService state on mount
          MapService.toggleLayer(key, this.layers[key].visible)
        }
      })
    }

    // Optional: Subscribe to some store or event to update counts dynamically
    this.updateCounts()
    // Poll for counts if data changes frequently outside reactive scope
    this.countInterval = setInterval(this.updateCounts, 500)
  },
  beforeDestroy() {
    if (this.countInterval) clearInterval(this.countInterval)
  },
  watch: {
    layersVisibility: {
      handler(newVal) {
        if (newVal) {
          Object.keys(newVal).forEach(key => {
            if (this.layers[key] && this.layers[key].visible !== newVal[key]) {
              this.layers[key].visible = newVal[key]
              MapService.toggleLayer(key, newVal[key])
            }
          })
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    toggleExpanded() {
      this.expanded = !this.expanded
    },
    toggleLayer(layerKey) {
      const isVisible = this.layers[layerKey].visible
      MapService.toggleLayer(layerKey, isVisible)

      // Notify parent component
      if (this.onVisibilityChange) {
        this.onVisibilityChange(layerKey, isVisible)
      }
    },
    updateCounts() {
      // Use the new getLayerCounts method from MapService
      const counts = MapService.getLayerCounts()

      if (counts && counts.total) {
        // Update counts generically for all layers defined in the component
        Object.keys(this.layers).forEach(layerKey => {
          if (counts.total[layerKey] !== undefined) {
            this.layers[layerKey].count = counts.total[layerKey]
          }
        })
      }
    },
    isLayerEnabled(layerKey) {
      if (!this.$parent || !this.$parent.generalMapPreferences) return true
      const enabledLayers = this.$parent.generalMapPreferences.entities || []
      return enabledLayers.includes(layerKey)
    },
    hasFilters(layerKey) {
      return !!(this.mapLayersConfig[layerKey] && this.mapLayersConfig[layerKey].filters)
    },
    openFilters(layerKey) {
      this.activeFiltersLayer = layerKey
      this.$nextTick(() => {
        this.$refs.layerFiltersModal.$refs.modal.show()
      })
    },
    applyFilters(data) {
      this.$emit('apply-filters', data)
    },
    updateFilters(newFilters) {
      // Manual update since Leaflet controls are not reactively linked to parent props
      this.$set(this, 'localActiveFilters', cloneDeep(newFilters))
    },
    getActiveFiltersCount(layerKey) {
      if (!this.localActiveFilters || !this.localActiveFilters[layerKey]) return 0
      const filters = this.localActiveFilters[layerKey]
      let count = 0
      Object.keys(filters).forEach(key => {
        const val = filters[key]
        if (val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
          count++
        }
      })
      return count
    },
    resetLayerFilters(layerKey) {
      this.$emit('apply-filters', {
        layerKey,
        filters: {}
      })
    }
  }
}
</script>

<style scoped>
.map-layer-control {
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 5px rgba(0,0,0,0.4);
  min-width: 220px;
  overflow: hidden;
  font-family: 'Helvetica Neue', Arial, Helvetica, sans-serif;
  font-size: 13px;
}

.header {
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  color: #333;
}

.header:hover {
  background: #e9ecef;
}

.layer-list {
  padding: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.section-divider {
  font-weight: bold;
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  margin: 10px 0 5px 0;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 2px;
}

.section-divider:first-child {
  margin-top: 2px;
}

.layer-item {
  margin-bottom: 6px;
}

.layer-item:last-child {
  margin-bottom: 0;
}

.layer-item label {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 0;
}

.layer-item input {
  margin-right: 8px;
}

.layer-item .btn-link:hover {
  color: #007bff !important;
  text-decoration: none;
}

.layer-label {
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 12px;
  color: #444;
}

.layer-indicators {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-width: 60px;
}

.item-count {
  font-size: 11px;
  font-weight: 600;
  color: #888;
  background: #f0f0f0;
  padding: 1px 6px;
  border-radius: 10px;
  min-width: 24px;
  text-align: center;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-btn {
  background: transparent;
  border: none;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  outline: none;
}

.filter-trigger {
  color: #ccc;
}

.filter-trigger:hover {
  background: #f0f0f0;
  color: #777;
}

.filter-trigger.has-active-filters {
  color: #007bff;
  background: #eef6ff;
}

.filter-trigger.has-active-filters:hover {
  background: #deedff;
}

.filter-count-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #007bff;
  color: white;
  font-size: 9px;
  font-weight: bold;
  min-width: 15px;
  height: 15px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
}

.reset-trigger {
  color: #ffb4b4;
  font-size: 12px;
}

.reset-trigger:hover {
  color: #dc3545;
  background: #fff0f0;
}

</style>
